import React from 'react';
import { TemplateType } from '../TemplateType';
import usePdfContent, { PdfContentParams } from '../usePdfContent';
import { useLocation } from 'react-router-dom';
import { makePath } from 'apps/Pdf/helpers/helpers';

import Grid from '@material-ui/core/Grid';

import PdfContent from 'apps/Pdf/components/common/PdfContent';
import Title from 'apps/Pdf/components/common/Title';
import BasicData from 'apps/Pdf/components/BasicData/BasicData';
import H1 from 'apps/Pdf/components/common/H1';
import H2 from 'apps/Pdf/components/common/H2';
import Spacer from 'apps/Pdf/components/common/Spacer';
import P from 'apps/Pdf/components/common/P';
import Glossary from 'apps/Pdf/components/Glossary/Glossary';
import Deposits from 'apps/Pdf/components/DespositsAndReserves/Deposits';
import GeneralDeposit from 'apps/Pdf/components/DespositsAndReserves/GeneralDeposit';
import PageBreak from 'apps/Pdf/components/common/PageBreak';
import Signature from 'apps/Pdf/components/Signature/Signature';
import PlanBucket from 'apps/Pdf/components/Bucket/PlanBucket';
import Loading from 'apps/Pdf/components/common/Loading';

import { FinancialQuestionnaireContent } from './FinancialQuestionnaireInterface';
import useGeneralStyles from 'apps/Pdf/styles/general';
import useColorStyles from 'apps/Pdf/styles/colors';
import Targets from 'apps/Pdf/components/Targets/Targets';
import FutureDeposits from 'apps/Pdf/components/DespositsAndReserves/FutureDeposits';
import Reserves from 'apps/Pdf/components/DespositsAndReserves/Reserves';
import { Currency } from 'enums';
import { PdfCategory } from '../Interfaces';

const FinancialQuestionnaire = (params: PdfContentParams) => {
  const styles = useGeneralStyles();
  const location = useLocation();
  const newQuest = makePath(TPL_NEW_FINANCIAL_QUESTIONNAIRE) === location.pathname.slice(0, -1);

  const pdfContent = usePdfContent<FinancialQuestionnaireContent>({
    category: PdfCategory.SIGNATURE,
    templateType: newQuest ? TPL_NEW_FINANCIAL_QUESTIONNAIRE : TPL_FINANCIAL_QUESTIONNAIRE,
    ...params,
  });

  const data = pdfContent?.data ? pdfContent.data : ({} as FinancialQuestionnaireContent);
  const currency = pdfContent?.data ? pdfContent.data.currency : Currency.CZK;
  const complete = !data.portfolioOnly;
  const colors = useColorStyles({ code: pdfContent?.data?.header.signedVia || '' });

  const riskChange =
    data.recommendedBucketSettings &&
    data.recommendedBucketSettings.some(
      (bucket) =>
        bucket.active && bucket.originalRiskProfile !== undefined && bucket.originalRiskProfile !== bucket.riskProfile,
    );

  const esgMismatch =
    data.esg &&
    ((data.recommendedBucketSettings[0].portfolio.code.profile === '1' &&
      data.recommendedBucketSettings[1]?.portfolio.code.profile === '1' &&
      data.recommendedBucketSettings[2]?.portfolio.code.profile === '1') ||
      (data.portfolioOnly && data.recommendedBucketSettings[2]?.portfolio.code.profile === '1'));

  const esgMismatchInfo = (
    <>
      <br />
      <strong>
        Upozornění: Vybrané portfolio neodpovídá Vašim požadavkům ohledně odpovědného investování dle ESG kritérií,
        které jste uvedl v investičním dotazníku!
      </strong>
    </>
  );

  return pdfContent ? (
    <PdfContent errors={pdfContent?.errors} className={colors.root} onload afterLoad={params.afterLoad}>
      <Title>Finanční dotazník a nastavení portfolií</Title>

      <BasicData data={pdfContent.data!.header} />

      <H1>Nastavení investičního plánu</H1>

      {complete ? (
        <P>
          Níže jsou uvedeny důležité údaje, na základě kterých budeme v průběhu času přeskupovat majetek mezi
          nastavenými portfolii. Je klíčové, aby tyto údaje odpovídaly vašim skutečným záměrům. V případě, že se některý
          z uvedených údajů změní, je důležité kontaktovat vašeho poradce a společně údaje aktualizovat.
        </P>
      ) : (
        <P>
          Níže jsou uvedeny důležité údaje, na základě kterých budeme v průběhu času spravovat Váš majetek. Je klíčové,
          aby tyto údaje odpovídaly vašim skutečným záměrům. V případě, že se některý z uvedených údajů změní, je
          důležité kontaktovat vašeho poradce a společně údaje aktualizovat.
        </P>
      )}

      <Deposits deposits={data.deposits} currency={currency} actualInflation={0} />

      {data.deposits.employerAllowed && data.deposits.employer && (
        <GeneralDeposit deposit={data.deposits.employer} currency={currency} actualInflation={0} type="employer" />
      )}

      <FutureDeposits data={data.deposits.deposits} currency={currency} actualInflation={0} />

      <Reserves data={data.reserves} currency={currency} />

      <Targets
        data={data.targets?.targets}
        rentData={data.rent}
        currency={currency}
        personType={data.personType}
        actualInflation={0}
      />

      <PageBreak />

      <H1>Nastavení portfolií</H1>
      {complete ? (
        <>
          <P>
            Níže je uvedeno předvybrané nastavení portfolií vycházející z Vašich odpovědí v investičním dotazníku s
            přihlédnutím k Vašim osobním preferencím, nebo Vaši volby jednotlivých investičních nástrojů z Portfolio
            managerem předvybrané množiny.
          </P>

          <div className={`${styles.threeColumns}`}>
            <PlanBucket
              term="longTerm"
              data={data.recommendedBucketSettings[2]}
              currency={currency}
              setup
              riskChange={riskChange}
            />
            <PlanBucket
              term="midTerm"
              data={data.recommendedBucketSettings[1]}
              currency={currency}
              setup
              riskChange={riskChange}
            />
            <PlanBucket
              term="shortTerm"
              data={data.recommendedBucketSettings[0]}
              currency={currency}
              setup
              riskChange={riskChange}
            />
          </div>

          <Spacer />

          <H2 className="longTerm">Dlouhodobé portfolio</H2>
          <P className="tight">
            Do Dlouhodobého portfolia budeme směřovat všechny peníze, které nejsou určeny k výběru v příštích
            10&nbsp;letech. Pokud tedy nemáte nastavený v příštích 10 letech žádný cíl nebo výběr renty, budeme
            do&nbsp;dlouhodobého portfolia směřovat všechny peníze, které investujete. Peníze budeme investovat podle
            vybrané investiční strategie.
            {esgMismatch && esgMismatchInfo}
          </P>

          <H2 className="midTerm">Střednědobé portfolio</H2>
          <P className="tight">
            Do Střednědobého portfolia budeme směřovat peníze, které plánujete vybrat za 4 až 10 let. Pokud tedy máte
            nastaveny cíle, začneme odpovídající částku do portfolia přesouvat v okamžiku, kdy se přiblíží na 10 let
            od&nbsp;své splatnosti a budeme ji v portfoliu držet, dokud se splatnost cíle nezkrátí na 3 roky. Pak částku
            na cíl přesuneme do krátkodobého portfolia. Pokud budete čerpat rentu, budeme ve střednědobém portfoliu
            držet vždy částku na 7 let vyplácení renty. V portfoliu držíme navíc vámi požadovanou rezervu, pokud jste si
            ji nastavili.
            {esgMismatch && esgMismatchInfo}
          </P>

          <H2 className="shortTerm">Krátkodobé portfolio</H2>
          <P className="tight">
            Do Krátkodobého portfolia budeme směřovat peníze, které plánujete vybrat v příštích 3 letech. Pokud tedy
            máte nastaveny cíle, začneme odpovídající částku do portfolia přesouvat v okamžiku, kdy se přiblíží na
            3&nbsp;roky od plánovaného výběru a budeme ji v portfoliu držet, dokud ji nevyberete na svůj účet. Pokud
            budete čerpat rentu, budeme v krátkodobém portfoliu držet vždy částku na 3 roky vyplácení renty. V portfoliu
            držíme navíc vámi požadovanou rezervu, pokud jste si ji nastavili.
            {esgMismatch && esgMismatchInfo}
          </P>
        </>
      ) : (
        <>
          <div className={`${styles.twoColumns} third`}>
            <PlanBucket term="longTerm" data={data.recommendedBucketSettings[2]} currency={currency} setup />
            <Grid item xs={12}>
              <P>
                Níže je uvedeno předvybrané nastavení portfolií vycházející z Vašich odpovědí v investičním dotazníku s
                přihlédnutím k Vašim osobním preferencím, nebo Vaši volby jednotlivých investičních nástrojů z Portfolio
                managerem předvybrané množiny.
                {esgMismatch && esgMismatchInfo}
              </P>
            </Grid>
          </div>
        </>
      )}

      <Glossary
        keys={[
          'plannedDeposits',
          'requiredReserves',
          'expectedValueOfProperty',
          'requiredRent',
          'portfolio',
          'strategy',
          'strategyRiskiness',
          'expectedReturn',
          'acceptedLoss',
          'currencyHedging',
          'automaticRebalancing',
          'esg',
          'portfolioManager',
        ]}
      />

      <PageBreak />

      <H1>Upozornění a závěrečná prohlášení</H1>

      <P>
        Uvedené údaje týkající se minulé výkonnosti mají pouze informativní charakter. Minulá výkonnost investičního
        nástroje nemůže být považována za jakýkoliv příslib výkonnosti budoucí. Veškeré údaje o minulé výkonnosti
        nezahrnují vliv poplatků a provizí, které snižují konečnou výkonnost investice. Pro investora je také významné
        měnové riziko, v jehož důsledku může hodnota investice provedená v jiné měně než v měně cíle vlivem výkyvů
        měnových kurzů v průběhu času kolísat a posilující měna cílů může mít negativní dopad na výsledný výnos
        investice. S investováním obecně je spojena určitá míra rizika. Všechny investice na kapitálových trzích nesou v
        sobě riziko kolísání hodnoty v průběhu času a riziko kapitálové ztráty, kdy investor nemusí dosáhnout
        požadovaného výnosu nebo získat zpět plnou výši investovaného kapitálu.
      </P>

      <H2>Prohlášení zákazníka a informace o způsobu vyplnění finančního dotazníku</H2>

      <P>
        Prohlašuji, že jsem obdržel/a stejnopis tohoto formuláře, že mé požadavky jsou správně zaznamenány a že
        informace, které jsem zde uvedl/a, jsou úplné a pravdivé.
      </P>

      <P>
        Zákazník prohlašuje, že dotazník vyplnil dobrovolně, že poskytnuté údaje jsou aktuální, přesné a úplné a
        zavazuje se informovat {data.header.intermediaryCompany.companyName} o případných změnách.
      </P>

      <P>
        Zákazník si je zároveň vědom toho, že uvedení neúplných nebo nepravdivých údajů ho vystavuje riziku realizace
        nevhodných obchodů nebo výběru nevhodné investiční strategie.
      </P>

      <P>
        Zákazník souhlasí s výsledkem „investičního dotazníku“, který odpovídá jeho rizikovému profilu a potvrzuje výběr
        investičních strategií, resp. jednotlivých investičních nástrojů pro jednotlivá portfolia.
      </P>

      <Signature protocol={data.signedInfo} />
    </PdfContent>
  ) : params.preview ? (
    <Loading />
  ) : null;
};

const TPL_FINANCIAL_QUESTIONNAIRE: TemplateType = 'financialQuestionnaire';
const TPL_NEW_FINANCIAL_QUESTIONNAIRE: TemplateType = 'newFinancialQuestionnaire';

export { TPL_FINANCIAL_QUESTIONNAIRE, TPL_NEW_FINANCIAL_QUESTIONNAIRE, FinancialQuestionnaire };
